import de from "react-phone-number-input/locale/de";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { FormProvider, useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import {
  ModernForm,
  FormFieldInfo,
  CenteredInput,
  PhoneInputStyles,
  ModernFormSection,
  FormFieldInfoPara,
  FormFieldInfoSpan,
  CenteredInputLabel,
  ModernFormContainer,
  FormFieldInfoHeading,
  ModerFormBtnContainer,
  ModernFormFieldWrapper,
  ModernFormFieldsSection
} from "../ModernVariant.styles";
import { PolicySection } from "../../PolicySection";
import { getPhoneCountryCodes } from "../../../services/utils";
import { useLeadAction } from "../../../hooks/useLeadAction";
import MoveBackButton from "../MoveButtons/move-back-button";
import { updateDoRemove } from "../../../store/slices/funnelInfo";
import MoveForwardButton from "../MoveButtons/move-forward-button";
import { updateIsZipSubmitted } from "../../../store/slices/zipInfo";
import {
  EmailSuggestion,
  SuggestionButton
} from "../../ContactInfoForm/ContactInfoForm.styles";
import { ContactFormCompleteSchema } from "../../../services/validation-schema";
import { useEmailSuggestion } from "../../../hooks/useEmailSuggestion";
import { generatePolicyScope } from "../../../services/helper";
import {
  NAME_INPUT_PROPS,
  EMAIL_INPUT_PROPS,
  onEnterKey
} from "../../../services/form-fields";

const ModernLeadForm = ({
  setLeadError,
  isLeadSubmitting,
  setIsLeadSubmitting
}) => {
  const dispatch = useDispatch();
  const { emailSuggestion, checkEmail, clearEmailSuggestion } =
    useEmailSuggestion();
  const { checkPhoneNumberValidity, handleSubmitLeadInfo } = useLeadAction();
  const { funnel } = useSelector((state) => state.funnelInfo);
  const countryOption = useSelector(
    (state) => state.countryOption.countryOption
  );
  const reclaimMode = useSelector((state) => state.reclaimMode);
  const { amPrivacyPolicy } = useSelector(
    (state) => state.globalPrivacyPolicyInfo
  );
  const countries = getPhoneCountryCodes(countryOption);
  const textElements = funnel.textElements;
  const {
    numberCheck,
    fieldTitleName,
    fieldTitleEmail,
    fieldTitlePhone,
    fieldPlaceholderName,
    fieldPlaceholderEmail,
    fieldPlaceholderPhone
  } = textElements;

  const policyScope = generatePolicyScope({ funnel, reclaimMode });

  const methods = useForm({
    resolver: yupResolver(ContactFormCompleteSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      policyCheck: false,
      policySectionDisplayDecider: !!policyScope
    }
  });
  const {
    register,
    setError,
    setFocus,
    setValue,
    handleSubmit,
    formState: { errors }
  } = methods;
  const { onBlur: onEmailBlur, onChange: onEmailChange } = register("email");

  const onSubmit = (formValues) => {
    if (checkEmail(formValues.email)?.blocking) return;
    const isValidPhoneNumber = checkPhoneNumberValidity({
      phoneNumber: formValues.phone,
      numberCheck,
      setError
    });
    if (!isValidPhoneNumber) return;
    handleSubmitLeadInfo({
      formValues,
      setLeadError,
      setIsLoading: setIsLeadSubmitting,
      amPrivacyPolicy
    });
  };

  return (
    <>
      <ModernFormContainer className="mx-auto d-flex flex-column flex-md-row flex-nowrap justify-content-center align-items-stretch">
        <ModernFormSection className="position-relative d-flex flex-column flex-nowrap justify-content-start align-items-start">
          <FormFieldInfo
            className="w-100 text-white d-flex flex-column flex-nowrap justify-content-center align-items-end"
            $bgColor={funnel.design?.modernContrastColor || ""}
          >
            <FormFieldInfoHeading className="w-100 mb-0">
              {funnel.design?.modernFormInfoLabelOne ||
                "Nur noch ein letzter Schritt"}
            </FormFieldInfoHeading>
            <FormFieldInfoPara className="m-0 w-100">
              {funnel.design?.modernFormInfoLabelTwo ||
                "Damit wir Sie kontaktieren können, benötigen wir noch einige persönliche Daten."}
            </FormFieldInfoPara>
            <FormFieldInfoSpan className="d-none d-md-inline-flex align-items-center">
              {funnel.design?.modernFormInfoLabelThree || "Formular ausfüllen"}
              <FontAwesomeIcon icon={faArrowRight} />
            </FormFieldInfoSpan>
          </FormFieldInfo>
        </ModernFormSection>
        <ModernFormFieldsSection
          className={`bg-white ${isLeadSubmitting ? "pe-none" : ""}`}
        >
          <FormProvider {...methods}>
            <ModernForm>
              <FormFieldInfoHeading className="w-100 mb-0">
                {funnel.design?.modernFormHeading || "persönliche Daten"}
              </FormFieldInfoHeading>
              <ModernFormFieldWrapper>
                <CenteredInputLabel
                  htmlFor="name"
                  className={`mt-0 mb-1 ${errors?.name && "text-danger"}`}
                >
                  {`${fieldTitleName || "Name"}*`}
                </CenteredInputLabel>
                <CenteredInput
                  id="name"
                  type="text"
                  placeholder={fieldPlaceholderName}
                  {...NAME_INPUT_PROPS}
                  enterKeyHint="next"
                  onKeyDown={onEnterKey(() => setFocus("email"))}
                  {...register("name")}
                />
              </ModernFormFieldWrapper>
              <ModernFormFieldWrapper>
                <CenteredInputLabel
                  htmlFor="email"
                  className={`mt-0 mb-1 ${
                    (errors?.email || emailSuggestion?.blocking) &&
                    "text-danger"
                  }`}
                >
                  {`${fieldTitleEmail || "E-Mail"}*`}
                </CenteredInputLabel>
                <CenteredInput
                  id="email"
                  type="email"
                  placeholder={fieldPlaceholderEmail}
                  {...EMAIL_INPUT_PROPS}
                  enterKeyHint="next"
                  onKeyDown={onEnterKey(() => setFocus("phone"))}
                  {...register("email")}
                  onBlur={(event) => {
                    onEmailBlur(event);
                    checkEmail(event.target.value);
                  }}
                  onChange={(event) => {
                    onEmailChange(event);
                    // Once a hint is shown, keep it in sync while correcting
                    if (emailSuggestion) checkEmail(event.target.value);
                  }}
                />
                {emailSuggestion && (
                  <EmailSuggestion
                    className={
                      emailSuggestion.blocking
                        ? "text-danger"
                        : "text-secondary"
                    }
                  >
                    Meinten Sie{" "}
                    <SuggestionButton
                      type="button"
                      onClick={() => {
                        setValue("email", emailSuggestion.email, {
                          shouldValidate: true
                        });
                        clearEmailSuggestion();
                      }}
                    >
                      {emailSuggestion.email}
                    </SuggestionButton>
                    ?
                  </EmailSuggestion>
                )}
              </ModernFormFieldWrapper>
              <ModernFormFieldWrapper>
                <CenteredInputLabel
                  htmlFor="phone"
                  className={`mt-0 mb-1 ${errors?.phone && "text-danger"}`}
                >
                  {`${fieldTitlePhone || "Telefonnummer"}*`}
                </CenteredInputLabel>
                <PhoneInputStyles
                  id="phone"
                  enterKeyHint="send"
                  // This form has no submit button, so Enter submits here
                  onKeyDown={onEnterKey(() => handleSubmit(onSubmit)())}
                  addInternationalOption={false}
                  defaultCountry={countries[0]}
                  countries={countries}
                  name={"phone"}
                  placeholder={fieldPlaceholderPhone}
                  error={errors?.phone?.message}
                  // for translation of country names in German
                  labels={de}
                />
                {errors?.phone?.type === "custom" && (
                  <small className="text-danger pt-1">
                    {errors.phone.message}
                  </small>
                )}
              </ModernFormFieldWrapper>
              {policyScope && <PolicySection policyScope={policyScope} />}
            </ModernForm>
          </FormProvider>
        </ModernFormFieldsSection>
      </ModernFormContainer>
      <ModerFormBtnContainer className="d-flex flex-row justify-content-between mx-auto">
        <MoveBackButton
          onBackClick={() => {
            dispatch(updateDoRemove({ isForm: true }));
            dispatch(updateIsZipSubmitted(false));
          }}
          btnBg={funnel.design?.modernContrastColor}
          isDisabled={isLeadSubmitting}
        />
        {/* for submit lead */}
        <MoveForwardButton
          isDisabled={
            Boolean(Object.keys(errors).length) ||
            emailSuggestion?.blocking ||
            isLeadSubmitting
          }
          handleClick={() => handleSubmit(onSubmit)()}
          buttonText={funnel.design?.modernSubmitButtonText || "Senden"}
          fill={false}
          isSubmitting={isLeadSubmitting}
          textColor={funnel.design?.modernSubmitButtonTextColor}
          btnBg={funnel.design?.modernSubmitButtonBackground}
          btnHoverBg={funnel.design?.modernSubmitButtonHoverBackground}
        />
      </ModerFormBtnContainer>
    </>
  );
};
export default ModernLeadForm;
